import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { fetchClientWithRetries } from '@tools/fetchClient';

const LOG = LoggerFactory.create('tools/csrf');

export const CSRF_TOKEN_STORAGE_KEY = 'csrfToken';

export const fetchCsrfToken = async () => {
  try {
    const response = await fetchClientWithRetries.fetch('/hub/api/verify', { credentials: 'include' });

    if (response.status === 200) {
      const jsonResponse = await response.json();
      return jsonResponse?.csrfToken;
    }
  } catch (e) {
    LOG.error('Error verifying access token', e);
  }

  return null;
};

const isQuotaExceededError = (error: unknown) => {
  return (
    error instanceof DOMException &&
    // everything except Firefox
    (error.code === 22 ||
      // Firefox
      error.code === 1014 ||
      // test name field too, because code might not be present
      // everything except Firefox
      error.name === 'QuotaExceededError' ||
      // Firefox
      error.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
    // acknowledge QuotaExceededError only if there's something already stored
    localStorage &&
    localStorage.length !== 0
  );
};

/**
 * Generate the CSRF token using a prefix so there is no collision when user logs in to different events in different
 * tabs
 */
const getCsrfTokenKey = (prefix: string) => `${prefix}.${CSRF_TOKEN_STORAGE_KEY}`;

export const getCsrfTokenForUser = (userId: string) => {
  if (typeof window !== 'undefined') {
    const key = getCsrfTokenKey(userId);
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      LOG.info('Error getting CSRF token from local storage. Falling back to session storage');
      // Fall back to sessionStorage if localStorage not supported
      return window.sessionStorage.getItem(key);
    }
  }
  return null;
};

export const persistCsrfTokenForUser = (userId: string, csrfToken: string) => {
  if (typeof window !== 'undefined') {
    if (csrfToken) {
      const key = getCsrfTokenKey(userId);
      try {
        window.localStorage.setItem(key, csrfToken);
      } catch (e) {
        if (isQuotaExceededError(e)) {
          LOG.error('Error persisting CSRF token', e);
        } else {
          LOG.info('Error persisting CSRF token in local storage. Falling back to session storage');
        }
        // Fall back to sessionStorage if localStorage not supported
        window.sessionStorage.setItem(key, csrfToken);
      }
    } else {
      LOG.warn(`Error persisting CSRF token locally as token value is ${csrfToken}`);
    }
  }
};

export const clearCsrfTokenForUser = (userId: string) => {
  if (typeof window !== 'undefined') {
    const key = getCsrfTokenKey(userId);
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      LOG.info('Error removing CSRF token from local storage. Falling back to session storage');
      window.sessionStorage.removeItem(key);
    }
  }
};
